/**
 * multi-variant-atc.js
 *
 * The Add to Cart button for multi-variant-quantity.
 *
 * On click it:
 *   1. Fires 'mvq:request-state' on the document
 *   2. Listens once for 'mvq:state' response from multi-variant-quantity.js
 *   3. Validates (including design_placement)
 *   4. Walks state.designProps for per-layer customisation properties
 *      (`_Design N type` + `_Design N area (sq in)`) and aggregates by type
 *   5. Submits all selected variants + up to three fee line items
 *      (Outline / Full Colour Embroidery / Print) to /cart/add as a native
 *      form POST via window.MVQCartForm (assets/cart-form-post.js). When
 *      files are attached to more than one size the add is split across two
 *      POSTs so the artwork uploads once — see the two-phase block comment
 *      below and assets/mvq-cart-resume.js.
 *
 * Fee mechanic:
 *   - state.designProps contains a flat dict of property name → value
 *     (everything written by design-placement-block.liquid to the form)
 *   - `_Design N type` carries the type family (exact match against
 *     TYPE_* constants). Underscore-prefixed because it's hidden from
 *     the customer in cart/checkout, but still freely readable here
 *     from the form.
 *   - `_Design N area (sq in)` is the per-design chargeable sq in.
 *   - totalGarmentQty = sum of all size quantities selected
 *   - For each non-zero bucket: feeQty = bucketSqIn × totalGarmentQty
 *     and the matching fee variant ID from window.themeFeeVariants is posted
 *   - Fee lines are hidden in cart via CSS, visible at checkout.
 */

(function () {
  'use strict';

  if (customElements.get('multi-variant-atc')) return;

  // ── Type → display string mapping ─────────────────────────────────────────
  // Must match the strings written by design-placement-block.liquid and
  // read by cart-fee-watcher.js. Keep these three files in sync.
  const TYPE_OUTLINE = 'Outline Embroidery';
  const TYPE_COLOUR  = 'Full Colour Embroidery';
  const TYPE_PRINT   = 'Print';

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, '£{{amount}}');
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function feeVariants() {
    return window.themeFeeVariants || { outline: 0, colour: 0, print: 0 };
  }

  // ── File-upload widget integration (upload-tagged garments) ─────────────────
  //
  // On upload-tagged products the Add-to-Cart is THIS button, not the
  // file-upload widget's own product-form submit path, so the widget's
  // `properties[File Upload]` photo would otherwise never reach the cart.
  // These pure helpers turn the widget's collected payload into the same
  // file-property shape already used for the design-placement preview.

  // The file properties the form must carry as multipart (Shopify hosts each
  // and returns a public URL). Empty when there is no uploaded file.
  //
  // Upload-tagged garments carry only `File Upload`. We deliberately omit
  // `Design Preview` here: for uploads the preview is the same image as the
  // uploaded file, so a second hosted copy would be redundant.
  function uploadFileProps(uploaded) {
    const fp = [];
    if (uploaded && uploaded.file) {
      fp.push({ key: 'File Upload', file: uploaded.file, filename: uploaded.file.name || 'upload' });
    }
    return fp;
  }

  // Merge the widget's text properties onto a garment line. No-op when nothing
  // was uploaded, so non-upload flows are untouched.
  //
  // We deliberately do NOT stamp `Kind` here. An upload-tagged garment is a
  // garment + design already — self-contained. cart-design-validation keys off
  // `Kind` to detect *standalone* designs that must be attached to a garment, so
  // stamping it would make the cart wrongly demand this line be assigned to
  // another item.
  function mergeUploadTextProps(properties, uploaded) {
    const out = { ...properties };
    if (uploaded && uploaded.file) {
      if (uploaded.designName) out['Design Name'] = uploaded.designName;
    }
    return out;
  }

  // ── Bundle helpers ─────────────────────────────────────────────────────────
  //
  // A bundle product (product.metafields.custom.bundle_size > 0) is added as
  // exactly N items across sizes, charged as one fixed price. The lines still
  // reach Shopify individually — `_bundle_id` is what lets the cart page
  // collapse them back into a single row, and `_bundle_size` records the target
  // the add was validated against. Underscore-prefixed so they stay hidden from
  // the customer in cart and checkout, matching `_colour` / `_size` /
  // `_mvq_batch`. bundleSize 0 means "not a bundle" and must be a no-op.
  //
  // Pure and unit-tested in tests/multi-variant-atc-upload.test.mjs.

  function bundleLineProps(properties, bundleId, bundleSize) {
    const size = parseInt(bundleSize, 10) || 0;
    if (size <= 0 || !bundleId) return { ...properties };
    return { ...properties, '_bundle_id': String(bundleId), '_bundle_size': String(size) };
  }

  // The gate the Add-to-Cart button enforces visually, re-checked here so a
  // stale button state can never post an invalid bundle.
  function bundleAddIsValid(totalQty, bundleSize) {
    const size = parseInt(bundleSize, 10) || 0;
    if (size <= 0) return true;
    return totalQty === size;
  }

  // ── Two-phase add: upload the artwork once ─────────────────────────────────
  //
  // A customer picking four sizes of one customised garment wants one photo
  // uploaded, not four copies of it. Shopify's cart API makes that awkward: a
  // theme can't ask Shopify to host a file without adding it to the cart, and
  // multipart /cart/add carries exactly one line item, so the hosted URL for
  // the file can only be learned from a *completed* add.
  //
  // The old code learned it with a second scripted POST, which is what drew the
  // Cloudflare challenge. Instead we split across the navigation we were
  // already doing:
  //
  //   POST 1  single-item form, first garment + the only copy of each file,
  //           return_to=/cart?mvq_resume=<token>
  //   ── page load ──  mvq-cart-resume.js reads the hosted URLs out of the
  //                    server-rendered cart DOM (no request at all)
  //   POST 2  items[] form, remaining garments with each URL stamped on as a
  //           plain string, plus the fee lines
  //
  // Both are top-level document requests, so neither is challengeable, and the
  // file bytes cross the wire once. `_mvq_batch` is how the resume step finds
  // the line POST 1 created; it's underscore-prefixed so it stays hidden from
  // the customer in cart and checkout.
  const PENDING_KEY = 'mvq_pending_add';
  const BATCH_PROP  = '_mvq_batch';

  // Non-cryptographic — it only has to be unique among this tab's own adds.
  function newBatchToken() {
    return 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  // Does this add need the two-phase flow? Only when files have to be uploaded
  // AND there is something left over for POST 2 to carry. A single size with no
  // fee lines is complete after POST 1, so it stays a one-navigation add.
  function needsResume(fileProps, garmentItems, feeItems) {
    if (!fileProps.length || !garmentItems.length) return false;
    return garmentItems.length > 1 || feeItems.length > 0;
  }

  // Pure: the sessionStorage payload POST 2 is rebuilt from. `items` are the
  // lines POST 1 does NOT add; `fileKeys` are the property keys whose hosted
  // URLs must be stamped onto every garment line in that list.
  function buildPendingPayload(token, garmentItems, feeItems, fileProps) {
    return {
      v:            1,
      token:        token,
      consumed:     false,
      garmentCount: garmentItems.length - 1,
      items:        [...garmentItems.slice(1), ...feeItems],
      fileKeys:     fileProps.map(fp => fp.key),
    };
  }

  // ── Walk designProps and bucket sq in by type ─────────────────────────────
  //
  // designProps shape (flat dict). Underscore-prefixed keys are hidden from
  // the customer in cart/checkout, but they're still keys on the form and
  // come through to us here unchanged:
  //   {
  //     'Design 1 name':            'My Logo',
  //     'Design 1 configuration':   '3.3"x3.0" Outline Embroidery on the Front',
  //     '_Design 1 type':           'Outline Embroidery',
  //     '_Design 1 side':           'Front',
  //     '_Design 1 size':           '3.3"x3.0"',
  //     '_Design 1 position':       'x: 50, y: 30',
  //     '_Design 1 area (sq in)':   '14',
  //     'Design 2 configuration':   '5.0"x5.0" Print on the Back',
  //     '_Design 2 type':           'Print',
  //     '_Design 2 area (sq in)':   '40',
  //     ...,
  //     'Customisation Fee':        '£41.00'
  //   }
  //
  // We read `_Design N type` directly (exact match against TYPE_*
  // constants). The customer-facing `Design N configuration` carries
  // the same info as a single readable string, but we don't parse it
  // here — the atomic underscored fields are the machine path.
  //
  // Returns: { outline: N, colour: N, print: N } in sq in (per garment).
  // Returns all zeros for Plain mode (no `_Design N type` keys present).

  function bucketSqInByType(designProps) {
    const totals = { outline: 0, colour: 0, print: 0 };
    if (!designProps) return totals;

    Object.keys(designProps).forEach(key => {
      const m = key.match(/^_Design (\d+) type$/);
      if (!m) return;
      const n        = m[1];
      const type     = designProps[key];
      const areaKey  = `_Design ${n} area (sq in)`;
      const areaRaw  = designProps[areaKey];
      if (!areaRaw) return;
      const area = parseInt(String(areaRaw).replace(/[^0-9]/g, ''), 10) || 0;
      if (area <= 0) return;

      if      (type === TYPE_OUTLINE) totals.outline += area;
      else if (type === TYPE_COLOUR)  totals.colour  += area;
      else if (type === TYPE_PRINT)   totals.print   += area;
      // Unknown types are silently dropped here; the placement block surfaces
      // an error before we ever reach this code path.
    });

    return totals;
  }

  class MultiVariantAtc extends HTMLElement {

    connectedCallback() {
      this.labelAtc      = this.dataset.atcLabel    || 'Add to cart';
      this.labelAdding   = this.dataset.addingLabel || 'Adding…';
      this.bundleSize    = parseInt(this.dataset.bundleSize, 10) || 0;
      this._submitting   = false;

      this.errorEl   = this.querySelector('[data-error]');
      this.atcBtn    = this.querySelector('[data-atc-btn]');
      this.btnText   = this.querySelector('[data-btn-text]');
      this.btnCount  = this.querySelector('[data-btn-count]');

      // Force type="button" so Dawn's product-form never submits on click
      if (this.atcBtn) this.atcBtn.type = 'button';

      this.atcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleClick();
      });

      // Neutralise any ancestor form submit so Dawn's FormData POST never fires
      const ancestorForm = this.closest('form');
      if (ancestorForm) {
        ancestorForm.addEventListener('submit', (e) => {
          if (this.contains(e.target) || e.target === this.atcBtn) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        }, true);
      }

      document.addEventListener('mvq:qty-changed', e => {
        const qty = e.detail?.totalQty || 0;
        if (qty > 0) {
          this.btnCount.textContent   = `(${qty})`;
          this.btnCount.style.display = '';
        } else {
          this.btnCount.style.display = 'none';
        }
        if (this.bundleSize > 0) this.applyBundleGate(qty);
      });

      // Bundles start blocked: nothing is selected yet, so the target cannot be met.
      if (this.bundleSize > 0) this.applyBundleGate(0);
    }

    // Blocks Add to Cart until the selection is exactly bundleSize, and says how
    // many are still needed. Non-bundle products never reach this.
    applyBundleGate(totalQty) {
      const remaining = this.bundleSize - totalQty;
      const complete  = remaining === 0;

      this.atcBtn.disabled = !complete;

      if (complete) {
        this.btnText.textContent    = this.labelAtc;
        this.btnCount.style.display = 'none';
        return;
      }

      // Over-selection is unreachable while the steppers cap at the target, but
      // the wording still has to make sense if it ever happens.
      this.btnText.textContent = remaining > 0
        ? `Select ${remaining} more ${remaining === 1 ? 'item' : 'items'}`
        : `Remove ${-remaining} ${-remaining === 1 ? 'item' : 'items'}`;
      this.btnCount.style.display = 'none';
    }

    handleClick() {
      if (this._submitting) return;
      this.hideError();

      const responses = [];
      const onState = e => responses.push(e.detail);
      document.addEventListener('mvq:state', onState);
      document.dispatchEvent(new CustomEvent('mvq:request-state'));
      document.removeEventListener('mvq:state', onState);

      if (responses.length === 0) {
        this.showError('Quantity selector not found on this page.');
        return;
      }

      this.submitWithState(responses[0]);
    }

    async submitWithState(state) {
      const {
        quantities,
        variants,
        selectedColour,
        sizeOptionIdx,
        designProps,
        designValid,
      } = state;

      const selected = Object.entries(quantities).filter(([, q]) => q > 0);
      if (selected.length === 0) {
        this.showError('Please select a quantity for at least one size.');
        return;
      }

      // The size table is the source of truth for the target; fall back to this
      // element's own attribute if an older widget doesn't broadcast it.
      const bundleSize = parseInt(state.bundleSize, 10) || this.bundleSize || 0;

      if (!designValid) return;

      // ── Collect the file-upload widget's photo (upload-tagged garments) ────
      // Absent widget → uploaded stays null and nothing below changes. A
      // present-but-invalid widget (no photo / bad name) surfaces its own
      // inline error and aborts the add.
      const uploader = document.querySelector('file-upload-dropzone-fileupload');
      let uploaded = null;
      if (uploader && typeof uploader.collectForCart === 'function') {
        const collected = uploader.collectForCart();
        if (!collected.valid) return;
        uploaded = collected;
      }

      // ── Build garment line items ──────────────────────────────────────────

      const garmentItems = [];
      const totalGarmentQty = selected.reduce((sum, [, qty]) => sum + qty, 0);

      // The disabled button is the UX; this is the correctness guarantee.
      if (!bundleAddIsValid(totalGarmentQty, bundleSize)) {
        this.showError(`This bundle needs exactly ${bundleSize} items — you have selected ${totalGarmentQty}.`);
        return;
      }

      // One id shared by every line of this add. newBatchToken() already exists
      // for the same "unique among this tab's own adds" requirement.
      const bundleId = bundleSize > 0 ? newBatchToken() : '';

      selected.forEach(([variantIdStr, qty]) => {
        const variantId = parseInt(variantIdStr);
        const variant   = variants.find(v => v.id === variantId);
        if (!variant) return;
        const sizeLabel = variant.options?.[sizeOptionIdx] ?? variant.title;

        garmentItems.push({
          id:       variantId,
          quantity: qty,
          properties: bundleLineProps(mergeUploadTextProps({
            ...designProps,
            '_colour': selectedColour || '',
            '_size':   sizeLabel,
          }, uploaded), bundleId, bundleSize),
        });
      });

      // ── Build fee line items (up to three) ────────────────────────────────

      const fv     = feeVariants();
      const totals = bucketSqInByType(designProps);
      const feeItems = [];
      [
        { id: fv.outline, sqIn: totals.outline },
        { id: fv.colour,  sqIn: totals.colour  },
        { id: fv.print,   sqIn: totals.print   },
      ].forEach(line => {
        if (!line.id || line.sqIn <= 0) return;
        const feeQty = line.sqIn * totalGarmentQty;
        if (feeQty <= 0) return;
        feeItems.push({ id: line.id, quantity: feeQty });
      });

      // Is this a customised order? Look for any `_Design N type` key.
      const hasDesigns = Object.keys(designProps || {})
        .some(k => /^_Design \d+ type$/.test(k));

      this.setLoading(true);
      this._submitting = true;

      try {
        // ── One native form POST ──────────────────────────────────────────
        //
        // This used to go over fetch: a multipart POST /cart/add.js for the
        // first garment + files, then a JSON POST for the remaining garments
        // and fee lines, then a navigation to /cart. That's a scripted
        // POST → POST → navigate → GET burst from one client inside a second
        // or two, which is exactly the shape Shopify's Cloudflare layer
        // challenges — the same finding is written up in cart-fee-watcher.js's
        // page gate. Worse, a challenge answers text/html, so res.json() threw
        // before anything looked at res.ok: the customer got a dead-end
        // "Something went wrong" with the first garment already in their cart.
        //
        // The flow always ended in a full navigation to /cart, so the fetch
        // bought nothing. Now we build a real hidden form and let the browser
        // submit it: ONE top-level document request, byte-for-byte what a
        // no-JS theme sends, with Shopify redirecting to `return_to`
        // afterwards. Nothing scripted touches the cart, so there's no
        // challenge to trip. It also sidesteps apps that monkey-patch
        // window.fetch to intercept /cart/add.js — what the old iframe-realm
        // "clean fetch" existed for.

        const fileProps = [];
        if (hasDesigns && garmentItems.length > 0) {
          const previewBlob = await this.getPreviewBlob();
          if (previewBlob) fileProps.push({ key: 'Preview', file: previewBlob, filename: 'design-preview.png' });
        }
        uploadFileProps(uploaded).forEach(fp => fileProps.push(fp));

        if (needsResume(fileProps, garmentItems, feeItems)) {
          // Phase 1 of the two-phase add — see the block comment above.
          const token = newBatchToken();
          const first = {
            id:         garmentItems[0].id,
            quantity:   garmentItems[0].quantity,
            properties: { ...garmentItems[0].properties, [BATCH_PROP]: token },
          };

          // Store before submitting: once form.submit() runs, this page is
          // gone and anything not persisted is lost.
          try {
            sessionStorage.setItem(
              PENDING_KEY,
              JSON.stringify(buildPendingPayload(token, garmentItems, feeItems, fileProps))
            );
          } catch (e) {
            // Storage disabled or over quota, so phase 2 could never run. Both
            // ways of pressing on produce a wrong order: dropping the files
            // gives production an order with no artwork, and dropping the extra
            // sizes silently under-delivers what the customer chose. So stop
            // and say so — adding one size at a time still works, since that
            // path needs no stored state.
            console.error('[MVQ-ATC] Could not persist the pending add', e);
            this.showError(
              'Your browser is blocking storage, so we can only add one size at a'
              + ' time. Please select a single size and add it, then repeat for'
              + ' the others.'
            );
            this.setLoading(false);
            this._submitting = false;
            return;
          }

          window.MVQCartForm.submitCartForm({
            single:   true,
            items:    [first],
            files:    fileProps,
            returnTo: '/cart?mvq_resume=' + encodeURIComponent(token),
          });
          return;
        }

        // Everything fits in one request: either no files to upload, or a
        // single garment line with no fee lines.
        window.MVQCartForm.submitCartForm({
          single:   fileProps.length > 0,
          items:    fileProps.length > 0 ? [garmentItems[0]] : [...garmentItems, ...feeItems],
          files:    fileProps,
          returnTo: '/cart',
        });

        // The navigation is in flight. Deliberately no `finally` resetting the
        // button: the old code did, so the label flipped back to "Add to cart"
        // mid-redirect and the button looked clickable again.

      } catch (err) {
        console.error('[MVQ-ATC] Cart error:', err);
        this.showError('Something went wrong. Please try again.');
        this.setLoading(false);
        this._submitting = false;
      }
    }

    // Ask design-placement-block to render its current front+back state to
    // a PNG Blob. Resolves to null on timeout or render failure — callers
    // must handle the null path.
    async getPreviewBlob() {
      return new Promise((resolve) => {
        let resolved = false;
        const respond = (blob) => {
          if (resolved) return;
          resolved = true;
          resolve(blob || null);
        };
        document.dispatchEvent(new CustomEvent('dp:request-preview', {
          detail: { respond }
        }));
        // Safety timeout in case no placement block is present
        // (e.g. design tag products that use the alternative flow).
        setTimeout(() => respond(null), 10000);
      });
    }

    setLoading(on) {
      this.atcBtn.disabled = on;
      this.atcBtn.classList.toggle('loading', on);

      if (on) {
        // Text change — guaranteed visual signal regardless of the
        // spinner state. If the spinner DOM never lands or gets clobbered
        // by a theme re-render, the customer still sees "Adding…" rather
        // than a frozen-looking button.
        if (this.btnText) {
          if (this._origText === undefined) this._origText = this.btnText.textContent;
          this.btnText.textContent = this.labelAdding;
        }
        if (this.btnCount) this.btnCount.style.display = 'none';

        if (!this._spinnerEl) {
          // Inline-block sibling of the button text. This is attempt
          // four — the previous three (CSS @keyframes, Web Animations
          // API, rAF + position:absolute overlay) all shared one variable:
          // absolute positioning inside the button. That's the variable
          // we're removing. The spinner sits in the button's natural
          // layout flow next to the text — if the text is visible, the
          // spinner is too. No positioning-context requirement, no
          // overflow:hidden risk, no z-index battles.
          this._spinnerEl = document.createElement('span');
          this._spinnerEl.setAttribute('aria-hidden', 'true');
          this._spinnerEl.style.cssText = [
            'display: inline-block',
            'width: 16px',
            'height: 16px',
            'margin-left: 10px',
            'vertical-align: -3px',
            'border: 2px solid currentColor',
            'border-top-color: transparent',
            'border-radius: 50%',
            'box-sizing: border-box',
            'will-change: transform',
            'pointer-events: none',
          ].join(';');
          this.atcBtn.appendChild(this._spinnerEl);
        } else {
          this._spinnerEl.style.display = 'inline-block';
        }

        // requestAnimationFrame writing the inline transform each frame.
        // No CSS animations, no Web Animations API — just JS-to-DOM.
        this._spinning = true;
        const startTime = performance.now();
        const spinEl   = this._spinnerEl;
        const tick = (now) => {
          if (!this._spinning) return;
          const deg = ((now - startTime) / 700) * 360 % 360;
          spinEl.style.transform = 'rotate(' + deg + 'deg)';
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      } else {
        this._spinning = false;
        if (this.btnText && this._origText !== undefined) {
          this.btnText.textContent = this._origText;
          this._origText = undefined;
        }
        if (this.btnCount) this.btnCount.style.display = '';
        if (this._spinnerEl) this._spinnerEl.style.display = 'none';
      }
    }

    showError(msg) {
      this.errorEl.textContent   = msg;
      this.errorEl.style.display = 'block';
      this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    hideError() {
      this.errorEl.style.display = 'none';
    }
  }

  customElements.define('multi-variant-atc', MultiVariantAtc);

  // Exported for the node unit tests; a no-op in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      uploadFileProps, mergeUploadTextProps, needsResume, buildPendingPayload,
      bundleLineProps, bundleAddIsValid,
    };
  }
})();
